import { Box, CircularProgress, DialogActions, IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import FeatherIcon from "feather-icons-react";
import { Formik } from "formik";
import React from "react";
import { useSnackbar } from "../../components/toast/index.jsx";
import { DraggableDialog } from "../../consts.js";
import { INSIGHT_CREATED_FAILED, INSIGHT_CREATED_SUCCESS } from "../../helpers/messages.js";
import Utils from "../../utils.js";
import { AddTrackerSchema } from "./validator.js";

const AddTracker = ({ id, trackers, open, onClose }) => {
    const showSnackbar = useSnackbar();
    const initialValues = {
        tracker: ''
    }

    /**
     * Submit Method to save a tracker
     */
    const submitForm = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            // check for duplicate insight name
            const found = trackers.find((t) => t.name.trim().toLowerCase() === values.tracker.trim().toLowerCase());
            if (found) {
                showSnackbar(`Insight "${values.tracker}" already exists.`, "error");
                setSubmitting(false);
                return;
            }
            const trackersCopy = [...trackers];
            const today = new Date(Date.now()).toISOString().substr(0, 10);
            trackersCopy.push({ name: values.tracker, created: today, phrases: [] });
            
            await Utils.genericMutation("updateCompany", {
                id: id,
                trackers: JSON.stringify(trackersCopy),
            });
            setSubmitting(false);
            showSnackbar(INSIGHT_CREATED_SUCCESS)
            onClose(values.tracker);
        } catch (error) {
            console.log(error)
            setSubmitting(false);
            showSnackbar(INSIGHT_CREATED_FAILED, "error")
        }
    };

    const handleClose = (event, reason) => {
        // Prevent closing the dialog on backdrop click or escape key press
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        event.stopPropagation();
        onClose(null);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="draggable-dialog-title"
            scroll="paper"
            disableEscapeKeyDown={true}
            fullWidth={true}
            maxWidth="xs"
            PaperComponent={DraggableDialog}
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle
                id="draggable-dialog-title"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                gap={1}
            >
                New Insight
                <IconButton
                    className="draggable-cancel"
                    size="small"
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        color: theme.palette.grey[400],
                        marginRight: "-.5rem",
                    })}
                >
                    <FeatherIcon icon="x" />
                </IconButton>
            </DialogTitle>

            <Formik
                enableReinitialize
                validationSchema={AddTrackerSchema}
                initialValues={initialValues}
                onSubmit={(values, actions) => {
                    submitForm(values, actions);
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    values,
                    errors,
                    touched,
                    isSubmitting,
                }) => (
                    <Box
                        component="form"
                        display="flex"
                        flexDirection="column"
                        flexGrow={1}
                        overflow="auto"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <DialogContent dividers sx={{ paddingY: 3 }}>
                            <TextField
                                id="outlined-basic"
                                variant="outlined"
                                fullWidth={true}
                                label="Insight"
                                required
                                name={"tracker"}
                                value={values.tracker || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.tracker && !!errors.tracker}
                                helperText={touched.tracker ? errors.tracker : ''}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress color="inherit" size={24} /> : "Done"}
                            </Button>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
}

export default AddTracker;